import { firestore } from "@/services/firestore";
import {
  AdminReassignShopOwnerRequest,
  CardContents,
  Integration,
  Integrators,
  Shop,
  LayoutAccess,
  CardColor,
  Terminology,
} from "@/types/v2";
import { Shop as ShopV1 } from "@/types/v1";
import { generic } from "./generic";
import { createConverter } from "@/repositories/common";
import {
  DEFAULT_CARD_PRIMARY_FIELD,
  DEFAULT_CARD_SECONDARY_FIELD,
  DEFAULT_CARD_TERTIARY_FIELD,
} from "@/lib/const";
import { config } from "@/config";
import { httpsCallables } from "@/repositories/httpsCallables";

export const shop = {
  ref: (shopId: string) => firestore.getRef<Shop>(`shops/${shopId}`),
  listShopsByUser: async (userId: string): Promise<Shop[]> => {
    const shops = await firestore.queryDocuments<Shop>(
      "shops",
      "users",
      "array-contains",
      userId,

      createConverter<ShopV1, Shop>(shop)
    );
    const asShop = await firestore.getDocument<Shop>(
      `shops/${userId}`,
      createConverter<ShopV1, Shop>(shop)
    );
    if (asShop) shops.push(asShop);

    return shops;
  },
  listShops: async () => {
    return firestore.listDocuments<Shop>(
      "shops",
      createConverter<ShopV1, Shop>(shop)
    );
  },
  subscribeShop: (id: string, callback: (doc: Shop | undefined) => void) => {
    return firestore.subscribeDocument<Shop>(
      `shops/${id}`,
      callback,
      createConverter<ShopV1, Shop>(shop)
    );
  },
  update: async (shopId: string, shopData: Partial<Shop>) => {
    return generic.update({ id: shopId, ...shopData }, `shops/${shopId}`);
  },
  adminCreateShop: async (data: {
    email: string;
    shopName: string;
    integrator?: Integrators;
    sendActivationEmailNow: boolean;
    loginLink: string;
  }) => {
    const result = await httpsCallables.adminCreateShop(data);
    return result.data;
  },
  adminReassignShopOwner: async (data: AdminReassignShopOwnerRequest) => {
    const result = await httpsCallables.adminReassignShopOwner(data);
    return result.data;
  },
  setStatuses: async (shopId: string, statuses: string[]) => {
    return shop.update(shopId, { statusesToSync: statuses });
  },
  isV1: (shop: ShopV1 | Shop): shop is ShopV1 => {
    return (shop as Shop).schemaVersion === undefined;
  },
  transformV1ToV2: (shopData: ShopV1): Shop => {
    const returnShop = {
      _isSoftMigrated: true,
      containers: shopData.containers,
      createdOn: shopData.createdOn,
      defaultTimer: shopData.defaultTimer,
      email: shopData.email,
      enabledFeatures: shopData.enabledFeatures,
      lastSyncedTimestamp: shopData.lastSyncedTimestamp,
      layouts: shopData.layouts,
      name: shopData.name,
      phone: shopData.phone,
      refreshTimeInSeconds: shopData.refreshTimeInSeconds ?? 300,
      repairOrders: shopData.repairOrders ?? [],
      shopId: shopData.shopId,
      id: shopData.shopId,
      schemaVersion: 2,
      statusesToSync: shopData.statuses?.map((status) => `${status}`) ?? [],
      cardConfig: {
        cardContents: {
          primaryField: (shopData.cardContents?.primaryField ??
            DEFAULT_CARD_PRIMARY_FIELD) as CardContents["primaryField"],
          secondaryField: (shopData.cardContents?.secondaryField ??
            DEFAULT_CARD_SECONDARY_FIELD) as CardContents["secondaryField"],
          tertiaryField: (shopData.cardContents?.tertinaryField ??
            DEFAULT_CARD_TERTIARY_FIELD) as CardContents["tertiaryField"],
        },
      },
      integrationKey: "prod",
      integration: "tekmetric",
    };
    const updateShop = { ...returnShop } as Shop;
    delete updateShop._isSoftMigrated;

    // Only update if we have a valid shopId
    if (returnShop.shopId) {
      shop.update(returnShop.shopId, updateShop);
    }

    return returnShop as Shop;
  },
  updateCardContent: async (
    shopId: string,
    fieldName: string,
    fieldValue: string,
    cardContents: Shop["cardConfig"]["cardContents"],
    shopData?: Shop
  ) => {
    if (!shopData) {
      shopData = await shop.get(shopId);
    }
    if (!shopData) return;
    return shop.update(shopId, {
      cardConfig: {
        ...shopData.cardConfig,
        cardContents: { ...cardContents, [fieldName]: fieldValue },
      },
    });
  },
  updateTimerUnits: async (
    shopId: string,
    timerUnits: Shop["cardConfig"]["timerUnits"],
    shopData?: Shop
  ) => {
    if (!shopData) {
      shopData = await shop.get(shopId);
    }
    if (!shopData) return;
    return shop.update(shopId, {
      cardConfig: { ...shopData.cardConfig, timerUnits },
    });
  },
  updateDefaultTimerValue: async (shopId: string, defaultTimer: string) => {
    return shop.update(shopId, { defaultTimer: defaultTimer });
  },
  updateCardLabelWrap: async (shopId: string, cardLabelWrap: string) => {
    return shop.update(shopId, { cardLabelWrap: cardLabelWrap });
  },
  updateCustomerUpdateEnabled: async (
    shopId: string,
    customerUpdateEnabled: string
  ) => {
    return shop.update(shopId, {
      customerUpdateEnabled: customerUpdateEnabled,
    });
  },
  updateCustomerUpdateTimes: async (
    shopId: string,
    customerUpdateTimes: string[]
  ) => {
    return shop.update(shopId, {
      customerUpdateTimes: customerUpdateTimes,
      shopTimezone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  },
  updateLayoutAccess: async (shopId: string, layoutAccess: LayoutAccess[]) => {
    return shop.update(shopId, { layoutAccess: layoutAccess });
  },
  updateCardColors: async (shopId: string, cardColors: CardColor[]) => {
    // Fetch the current cardConfig
    const shopDoc = await shop.get(shopId);
    const cardConfig = shopDoc?.cardConfig || ({} as Shop["cardConfig"]);

    // Update the cardColors while keeping other properties intact
    cardConfig.cardColors = cardColors;

    // Update the entire cardConfig object
    return shop.update(shopId, { cardConfig });
  },
  updateTerminology: async (shopId: string, terminology: Terminology) => {
    const shopDoc = await shop.get(shopId);
    const shopConfig = shopDoc?.shopConfig || ({} as Shop["shopConfig"]);
    const updatedShopConfig = { ...shopConfig, terminology };
    return shop.update(shopId, { shopConfig: updatedShopConfig });
  },
  get: (shopId: string) => {
    return firestore.getDocument<Shop>(
      `shops/${shopId}`,
      createConverter<ShopV1, Shop>(shop)
    );
  },
  generateIntegration: (shop: Shop): Integration => {
    const integration = shop.integration ?? "tekmetric";
    const integrationKey = shop.integrationKey ?? "prod";

    const integrationConfig = config.integrations[integration];

    return {
      integration: integration,
      apiUrl: integrationConfig.urls.api[integrationKey] ?? "",
      linkUrl: integrationConfig.urls.links[integrationKey] ?? "",
      apiKey: integrationConfig.urls.apiKeys[integrationKey] ?? "",
      partnerId: integrationConfig.partnerIds
        ? (integrationConfig.partnerIds[integrationKey] ?? "")
        : "",
      tenantId: shop.tenantId,
    };
  },
};
